import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import toast, { Toaster, ToastBar } from "react-hot-toast";

import type { Route } from "./+types/root";
import { AuthProvider } from "./contexts/AuthContext";
import { EnvironmentProvider } from "./contexts/EnvironmentContext";
import { PostHogProvider } from "./components/providers/PostHogProvider";
import { SessionExpiredView } from "./components/SessionExpiredView";
import { ReadOnlyBanner } from "./components/ReadOnlyBanner";
import { is_auth_expired_error } from "./lib/auth-error-detection";
import { get_posthog } from "./lib/posthog.client";
import stylesheet from "./app.css?url";
import type { Environment } from "~/types/domain";

export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/icon.svg", type: "image/svg+xml" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  { rel: "stylesheet", href: stylesheet },
];

/**
 * Root loader that provides environment to all child routes via context.
 * This prevents hydration mismatches by ensuring server and client
 * use the same environment for domain URL generation.
 */
export function loader({ context }: Route.LoaderArgs) {
  const environment = (context.env.ENVIRONMENT as Environment) ?? undefined;
  return { environment, read_only_banner: context.read_only_banner ?? null };
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" data-theme="orchestraight">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <Meta />
        <Links />
        {/* Tolt affiliate tracking - must load before auth flow */}
        <script
          src="https://cdn.tolt.io/tolt.js"
          data-tolt={import.meta.env.VITE_TOLT_PUBLIC_ORG_KEY || ""}
          async
        />
      </head>
      <body className="bg-base-100">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { environment, read_only_banner } = useLoaderData<typeof loader>();

  return (
    <EnvironmentProvider environment={environment}>
      <AuthProvider>
        <Toaster
          position="top-center"
          containerStyle={{ top: 56 }}
          gutter={12}
          toastOptions={{
            icon: undefined,
            iconTheme: {
              primary: "oklch(0.5832 0.1567 151.05)",
              secondary: "oklch(1 0 0)",
            },
            error: {
              iconTheme: {
                primary: "oklch(0.6507 0.2204 35.08)",
                secondary: "oklch(1 0 0)",
              },
            },
          }}
        >
          {(t) => (
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <>
                  {icon}
                  {message}
                  {t.type !== "loading" && (
                    <button
                      className="btn btn-ghost btn-sm btn-circle"
                      onClick={() => toast.dismiss(t.id)}
                      aria-label="Dismiss notification"
                    >
                      ✕
                    </button>
                  )}
                </>
              )}
            </ToastBar>
          )}
        </Toaster>
        <PostHogProvider>
          <Outlet />
        </PostHogProvider>
        <ReadOnlyBanner banner={read_only_banner} />
      </AuthProvider>
    </EnvironmentProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Auth-expiry errors get a dedicated re-login surface instead of the
  // generic "Oops!" view. See `lib/auth-error-detection.ts`.
  if (is_auth_expired_error(error)) {
    return <SessionExpiredView />;
  }

  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;
  let status_code: number | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
    status_code = error.status;
  } else if (error && error instanceof Error) {
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }

    // Capture exception to PostHog Error Monitoring (client-side only)
    if (typeof window !== "undefined") {
      const posthog = get_posthog();
      if (posthog) {
        posthog.captureException(error, { route_error: false });
      }
    }
  }

  // Capture route errors to PostHog Error Monitoring (client-side only)
  if (typeof window !== "undefined" && isRouteErrorResponse(error)) {
    const posthog = get_posthog();
    if (posthog) {
      posthog.captureException(
        new Error(`RouteError ${error.status}: ${error.statusText || "Unknown"}`),
        { route_error: true, status_code },
      );
    }
  }

  return (
    <main className="flex flex-col items-center justify-center min-h-[50vh] px-4 pt-16 text-center container mx-auto">
      <h1 className="text-xl font-bold text-error">{message}</h1>
      <p className="text-sm text-base-content/60 mt-2 max-w-sm">{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto mt-4 text-left">
          <code className="text-xs">{stack}</code>
        </pre>
      )}
    </main>
  );
}
